import { DEFAULT_DIVIDER_CONTENT } from './lectureNormalization';
import { ALLOWED_BLOCK_TYPES } from './boppps/validateDeck';
import {
  BLOCK_TYPE_SPECS,
  BOPPPS_SECTIONS,
  DECK_FORMAT,
  DECK_SCHEMA_TYPE,
  DECK_VERSION,
  REJECTED_BLOCK_TYPES
} from './deckContract';
import { WORKSHEET_QUESTION_TYPES } from './worksheetQuestionTypes';

export const APP_SCHEMA_VERSION = '4.0.0';
export const BLANK_TEMPLATE_EXPORT_TYPE = 'blank_template_for_llm';

const CONTENT_KEYS = new Set([
  'content',
  'title',
  'subtitle',
  'caption',
  'prompt',
  'question',
  'answer',
  'text',
  'description',
  'notes',
  'label',
  'instructions',
  'instruction',
  'alt',
  'src',
  'href',
  'url',
  'link',
  'primaryTitle',
  'secondaryTitle',
  'callout',
  'summary',
  'footer',
  'header',
  'cite',
  'citation',
  'attribution',
  'author',
  'source',
  'imageTitle',
  'imageAuthor',
  'imageSource',
  'imageDate',
  'videoUrl',
  'audioUrl',
  'fileName'
]);

const deepClone = (value) => JSON.parse(JSON.stringify(value ?? {}));

const blankContentValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => {
      if (typeof entry === 'string') return '';
      if (entry && typeof entry === 'object') return blankContentValue(entry);
      return entry;
    });
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, entry]) => {
      if (CONTENT_KEYS.has(key)) {
        if (Array.isArray(entry)) {
          acc[key] = entry.map((item) => (typeof item === 'string' ? '' : blankContentValue(item)));
          return acc;
        }
        if (entry && typeof entry === 'object') {
          acc[key] = blankContentValue(entry);
          return acc;
        }
        acc[key] = '';
        return acc;
      }
      acc[key] = blankContentValue(entry);
      return acc;
    }, {});
  }
  return value;
};

const blankBlockContent = (block = {}) => {
  if (!block || typeof block !== 'object') return block;
  if (block.type === 'divider') {
    const normalized = block.content && typeof block.content === 'object'
      ? { ...DEFAULT_DIVIDER_CONTENT, ...block.content }
      : DEFAULT_DIVIDER_CONTENT;
    return { ...block, content: normalized };
  }
  return blankContentValue(block);
};

const blankSectionContent = (section = {}) => ({
  ...section,
  title: section?.title,
  text: '',
  blocks: (Array.isArray(section.blocks) ? section.blocks : []).map(blankBlockContent)
});

const blankSectionsPayload = (sections) => {
  if (Array.isArray(sections)) {
    return sections.map(blankSectionContent);
  }
  if (sections && typeof sections === 'object') {
    return Object.entries(sections).reduce((acc, [key, value]) => {
      if (Array.isArray(value)) {
        acc[key] = value.map(blankBlockContent);
        return acc;
      }
      if (value && typeof value === 'object') {
        acc[key] = blankSectionContent({ ...value, id: value.id || key });
        return acc;
      }
      acc[key] = value;
      return acc;
    }, {});
  }
  return sections;
};

export const buildBlankTemplateData = (lessonJson = {}) => {
  const cloned = deepClone(lessonJson);

  cloned.courseTopic = '';
  cloned.instructorName = '';
  cloned.instructorEmail = '';
  cloned.footerCourseInfo = '';
  cloned.footerInstitution = '';
  cloned.footerCopyright = '';
  cloned.slideDeckName = '';
  cloned.exportFileName = '';
  cloned.htmlExportTitle = '';
  cloned.schoolLogo = null;
  cloned.schoolLogoName = '';
  cloned.schoolLogoSize = 0;

  if (cloned.slideNames && typeof cloned.slideNames === 'object') {
    cloned.slideNames = Object.keys(cloned.slideNames).reduce((acc, key) => {
      acc[key] = '';
      return acc;
    }, {});
  }

  cloned.titleCardSettings = blankContentValue(cloned.titleCardSettings || {});
  cloned.endTitleCardSettings = blankContentValue(cloned.endTitleCardSettings || {});

  cloned.sections = blankSectionsPayload(cloned.sections);

  return cloned;
};

/**
 * Instructions embedded in the blank-template export.
 *
 * The prose here is about *this* workflow — fill the existing structure, do not
 * restructure it. Everything factual about the schema (which block types exist,
 * what each one needs, which worksheet question types are valid) is pulled from
 * `deckContract`, which derives it from the validator itself.
 *
 * The hand-written version this replaced had drifted a long way from the code:
 * it listed 14 of the 25 supported block types, declared `video` and `image`
 * unsupported when both are supported, contradicted itself by documenting
 * `apa-citation` in one section and omitting it from the valid list in another,
 * and described worksheet question types (`shortAnswer`, `multipleChoice`) that
 * had been renamed two schema versions earlier.
 */
const buildLlmTemplateInstructions = () => ({
  overview: `This is a blank BOPPPS Lesson Planner template for AI-assisted content generation.

The structure is already correct. Your job is to FILL IN the empty content fields —
not to restructure, add blocks, or change ids.

You will also receive a companion content pack containing the lecture outline. Use it
as the source of truth for topics, sequence and instructional intent, and expand it
into a fully developed, student-facing lesson.`,

  critical_rules: {
    rule_1: 'DO NOT modify any existing "id" field.',
    rule_2: 'DO NOT modify any existing "slideId" field — slideId is what groups blocks onto a slide.',
    rule_3: 'DO NOT add, remove or reorder blocks. Populate the ones that are there.',
    rule_4: 'DO NOT change any block "type".',
    rule_5: 'ONLY replace empty strings ("") with content.',
    rule_6: 'The "slideNames" and "slideVisibility" objects are already correct — leave them alone.',
    rule_7: 'Keep every array field (items, cardItems, stackedCardItems, accordionItems, questions), even when empty.',
    rule_8: 'No square brackets anywhere. "[topic]" and the like are rejected on import as unfilled placeholders.'
  },

  workflow: {
    step1: 'Read the companion content pack for the topics, outcomes and instructional flow.',
    step2: 'Find each block with empty content fields (content: "", title: "").',
    step3: 'Fill only those fields, based on the outline.',
    step4: 'For blocks with items / cardItems / stackedCardItems arrays, populate the entries inside them.',
    step5: 'Check every id is unchanged and the JSON parses before returning it.'
  },

  slide_sizing: {
    rule: 'A slide holds roughly one headline plus 4-6 bullets, or 2-3 short paragraphs.',
    why: 'Longer slides are scaled down to fit the screen in presentation mode and become hard to read.',
    note: 'You cannot add slides in this workflow — so keep each block within the space its slide has.'
  },

  // Derived from the validator; see src/utils/deckContract.js.
  block_types_reference: Object.entries(BLOCK_TYPE_SPECS).reduce((acc, [type, spec]) => {
    acc[type] = { use: spec.use, shape: spec.shape };
    return acc;
  }, {}),

  supported_block_types: {
    description: 'ONLY these block types are valid. Any other type is rejected on import.',
    valid_types: [...ALLOWED_BLOCK_TYPES]
  },

  rejected_block_types: REJECTED_BLOCK_TYPES,

  worksheet_question_types: {
    shape: '{ "type": ..., "title": "The question", "points": 0 }  // mcq also takes "options": []',
    valid_types: WORKSHEET_QUESTION_TYPES.map(({ value, label }) => `${value} — ${label}`)
  },

  schema_requirements: {
    required_top_level: [
      `format: "${DECK_FORMAT}"`,
      `version: "${DECK_VERSION}"`,
      `schema_type: "${DECK_SCHEMA_TYPE}"`,
      'timestamp: ISO date string',
      'sections: object keyed by section id, each { id, title, blocks: [] }',
      'slideNames: object mapping slideId to display name',
      'slideVisibility: object mapping slideId to boolean'
    ],
    required_block_fields: [
      'id — unique block identifier (DO NOT CHANGE)',
      'type — block type (DO NOT CHANGE)',
      'slideId — which slide the block renders on (DO NOT CHANGE)',
      'content or the type-specific array (FILL THIS IN)',
      'items, cardItems, stackedCardItems, accordionItems, questions — must exist even if empty'
    ],
    sections: BOPPPS_SECTIONS.map((section) => `${section.id}: ${section.purpose}`)
  },

  content_guidelines: {
    html_formatting: [
      'Wrap paragraphs in <p> tags',
      'Use <strong> for bold, <em> for italic',
      'Use <ul><li> for bullets and <ol><li> for numbered lists',
      'Close every tag'
    ],
    voice: [
      'Write to the learner in the second person, not as notes to the instructor',
      'Expand outline points into real explanations with concrete examples',
      'Do not invent statistics, citations, URLs or quotations',
      'Leave media src/url fields empty for the instructor and describe what belongs there'
    ]
  },

  validation_checklist: [
    '1. Every original block id and slideId is unchanged',
    '2. slideNames and slideVisibility keys still match the slideIds used in blocks',
    '3. Every block still has id, type, slideId and its content field',
    '4. Every block still has items, cardItems, stackedCardItems, accordionItems, questions',
    '5. All HTML is well formed with closed tags',
    '6. No square brackets remain anywhere',
    '7. No empty strings remain where content was asked for',
    '8. The JSON parses',
    `9. format is "${DECK_FORMAT}" and version is "${DECK_VERSION}"`
  ]
});

export const buildBlankTemplateExport = ({ templateJson, templateName, description, schemaVersion }) => ({
  metadata: {
    templateName: templateName || 'BOPPPS Template',
    description: description || '',
    schema_version: schemaVersion || templateJson?.version || APP_SCHEMA_VERSION,
    exportType: BLANK_TEMPLATE_EXPORT_TYPE,
    llm_instructions: buildLlmTemplateInstructions()
  },
  ...templateJson
});
